/**
 * components/ThesisSummaryPanel.tsx — MER Algo Footprints v7.1H
 * Hedge Fund Mode — Section 4
 *
 * Compresses thesis into four structured blocks. No long scrolling narrative.
 * Each block is one or two lines maximum in v1.
 */

import React from 'react';
import { HedgeFundModeViewModel } from '../types/hedgeFundViewModel';

interface Props { vm: HedgeFundModeViewModel; }

interface BlockProps {
  label:   string;
  content: string;
  accent?: string;
  mono?:   boolean;
}

function ThesisBlock({ label, content, accent, mono }: BlockProps) {
  const isEmpty = !content || content === '—';
  return (
    <div style={{
      flex: 1,
      minWidth: 180,
      padding: '10px 12px',
      background: 'var(--surface2)',
      border: '1px solid var(--border)',
      borderLeft: `2px solid ${accent ?? 'var(--border)'}`,
      borderRadius: 4,
    }}>
      <div style={{
        fontSize: 8, fontWeight: 700, color: accent ?? 'var(--blue-dim)',
        fontFamily: 'var(--mono)', letterSpacing: 1.2, marginBottom: 5,
        textTransform: 'uppercase',
      }}>
        {label}
      </div>
      <div style={{
        fontSize: mono ? 11 : 12,
        color: isEmpty ? 'var(--blue-dim)' : 'var(--text)',
        fontFamily: mono ? 'var(--mono)' : 'inherit',
        lineHeight: 1.5,
      }}>
        {isEmpty ? 'No data' : content}
      </div>
    </div>
  );
}

export function ThesisSummaryPanel({ vm }: Props) {
  const { thesis } = vm;
  return (
    <div style={{
      background: 'var(--surface1)',
      border: '1px solid var(--border)',
      borderRadius: 6,
      padding: '12px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    }}>
      <div style={{
        fontSize: 9, fontWeight: 700, color: 'var(--purple)',
        fontFamily: 'var(--mono)', letterSpacing: 1.2,
      }}>
        AI THESIS SUMMARY
      </div>

      {/* Primary thesis — spans full width */}
      <div style={{
        padding: '10px 12px',
        background: 'rgba(139,92,246,0.06)',
        border: '1px solid rgba(139,92,246,0.2)',
        borderRadius: 4,
        fontSize: 12, color: 'var(--text)', lineHeight: 1.6,
      }}>
        {thesis.summary || 'No thesis available — run AI analysis first'}
      </div>

      {/* Three detail blocks */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <ThesisBlock
          label="What Changed"
          content={thesis.whatChanged}
          accent="var(--gold)"
        />
        <ThesisBlock
          label="Confirmation Needed"
          content={thesis.confirmation}
          accent="var(--blue)"
          mono
        />
        <ThesisBlock
          label="Invalidation Rule"
          content={thesis.invalidationRule}
          accent="var(--red)"
          mono
        />
      </div>
    </div>
  );
}
